import React, { Component } from "react";
import { connect } from "react-redux";
import moment from "moment";
import { isEmpty, sortBy } from "ramda";

import stemifyRoutes from "constants/routes";
import { progressWheelColorRange, CalculateProgressIndicatorColor } from "constants/common";
import Grid, { Col } from "styles/components/Grid";
import SectionDashboard, { SectionBody } from "styles/components/SectionDashboard";
import { FrameBox } from "styles/components/Frame";
import { ListStats, ListItem } from "styles/components/List";
import Stat, { StatTitle, StatEntry } from "styles/components/Stat";
import Loader from "components/common/loader";
import { NoDataMessageContainer } from "styles/components/Card";
import { NoDataMessage } from "styles/components/Card";
import { FORMAT } from "constants/common";
import StudentInfo, {
  StudentInfoTitle,
  StudentInfoEntry,
  StudentInfoIcon,
  StudentInfoContent,
} from "styles/components/StudentInfo";
import StudentAttempt, {
  StudentAttemptHead,
  StudentAttemptBody,
  StudentAttemptFoot,
  StudentAttemptTitle,
  StudentAttemptDetail,
  StudentAttemptDetailTitle,
  StudentAttemptDetailText,
  StudentAttemptScore,
  StudentAttemptScoreDetail,
  Button,
} from "styles/components/StudentAttempt";
import Section, { Card } from "styles/components/studentDashboard";
import {
  getAssessmentStudentDashboardData,
  clearAssessmentStudentDashboardData,
} from "store/state/facultyDashboard/actions";

class AssessmentDashboardStudent extends Component {
  state = {
    colorRange: progressWheelColorRange,
  };

  async componentDidMount() {
    const { match, getAssessmentStudentDashboardData } = this.props;
    const { deploymentId, assessmentId, studentId } = match.params;

    getAssessmentStudentDashboardData({
      deploymentId,
      assessmentId,
      studentId,
    });
  }

  componentWillUnmount() {
    const { clearAssessmentStudentDashboardData } = this.props;

    clearAssessmentStudentDashboardData();

    if (this.chartReference) {
      const { chartInstance } = this.chartReference.chartReference;
      chartInstance.destroy();
    }
  }

  render() {
    const { colorRange } = this.state;
    const { location, match, studentDetails, apiCallInProgress, studentApiErrors } = this.props;
    const { deploymentId, assessmentId, studentId } = match.params;

    const pathToProgress = studentDetails?.topicProgress?.map((item) => {
      return {
        ...item,
        pathname: `/learning-library`,
        search: `?id=${item.topic_id}&type=${item.type}&studentUsername=${studentDetails.id}`,
      };
    });
    // Sorting to show progress card from lowest to highest percent_score
    const myPathToProgress = sortBy((obj) => obj.percent_score, pathToProgress);

    const isAttemptDataLoaded =
      !apiCallInProgress &&
      studentApiErrors.get.code === -1 &&
      !isEmpty(studentDetails) &&
      studentDetails.attempts.length > 0;

    return isAttemptDataLoaded ? (
      <SectionDashboard modified>
        <SectionBody>
          <Grid gutterSmall colSpacing>
            <Col flex>
              <FrameBox variant="primary">
                <ListStats stats>
                  <ListItem key={"student-info"}>
                    <StudentInfo>
                      <StudentInfoIcon>{studentDetails.name.charAt(0)}</StudentInfoIcon>
                      <StudentInfoContent>
                        <StudentInfoTitle>{studentDetails.name}</StudentInfoTitle>
                        <StudentInfoEntry>{studentDetails.email}</StudentInfoEntry>
                      </StudentInfoContent>
                    </StudentInfo>
                  </ListItem>

                  <ListItem key="latestScore">
                    <Stat>
                      <StatTitle>{studentDetails.topScorePercentage}%</StatTitle>
                      <StatEntry>TOP SCORE</StatEntry>
                    </Stat>
                  </ListItem>

                  <ListItem key="numberOfAttempts">
                    <Stat>
                      <StatTitle>{studentDetails.totalAttemptCount}</StatTitle>
                      <StatEntry># OF ATTEMPTS</StatEntry>
                    </Stat>
                  </ListItem>

                  <ListItem key="latestAttempt">
                    <Stat>
                      <StatTitle>
                        {moment(studentDetails.latestAttemptDate).format(FORMAT.SHORT_DATE)}
                      </StatTitle>
                      <StatEntry>LATEST ATTEMPT</StatEntry>
                    </Stat>
                  </ListItem>

                  <ListItem key="durationOfLatest">
                    <Stat>
                      <StatTitle>{studentDetails.latestDuration}</StatTitle>
                      <StatEntry>DURATION OF LATEST</StatEntry>
                    </Stat>
                  </ListItem>
                </ListStats>
              </FrameBox>
            </Col>

            <Section.Head>
              <Section.HeadContent>
                <Section.Title>Attempts</Section.Title>
              </Section.HeadContent>
            </Section.Head>
            {/* Attempts */}
            <div style={{ display: "flex", flexWrap: "wrap" }}>
              {studentDetails.attempts.map((attempt, index) => (
                <Col flex third key={attempt.duration + index} style={{ maxWidth: "400px" }}>
                  <StudentAttempt>
                    <StudentAttemptHead>
                      <StudentAttemptTitle>
                        <div>Attempt #{index + 1}</div>
                        <div
                          style={{ fontSize: "12px", textAlign: "center", fontWeight: "normal" }}
                        >
                          Version {attempt.versionNumber}
                        </div>
                      </StudentAttemptTitle>

                      <Button
                        to={`${stemifyRoutes.deployments}/${deploymentId}/${assessmentId}/${studentId}/${attempt.attemptId}`}
                      >
                        View Details
                      </Button>
                    </StudentAttemptHead>

                    <StudentAttemptBody>
                      <StudentAttemptDetail>
                        <StudentAttemptDetailTitle>ATTEMPT SCORE</StudentAttemptDetailTitle>
                        <StudentAttemptScore>{attempt.scorePercentage}%</StudentAttemptScore>
                        <StudentAttemptScoreDetail>
                          ({attempt.score} / {attempt.totalScore})
                        </StudentAttemptScoreDetail>
                      </StudentAttemptDetail>
                    </StudentAttemptBody>

                    <StudentAttemptFoot>
                      <StudentAttemptDetail>
                        <StudentAttemptDetailTitle>Date</StudentAttemptDetailTitle>
                        <StudentAttemptDetailText>
                          {moment(attempt.date).format(FORMAT.SHORT_DATE)}
                        </StudentAttemptDetailText>
                      </StudentAttemptDetail>

                      <StudentAttemptDetail>
                        <StudentAttemptDetailTitle>Duration</StudentAttemptDetailTitle>
                        <StudentAttemptDetailText>{attempt.duration}</StudentAttemptDetailText>
                      </StudentAttemptDetail>
                    </StudentAttemptFoot>
                  </StudentAttempt>
                </Col>
              ))}
            </div>

            {/* Topic Progress */}
            {pathToProgress.length > 0 && (
              <>
                <Section.Head>
                  <Section.HeadContent>
                    <Section.Title>Topic Progress</Section.Title>
                  </Section.HeadContent>
                </Section.Head>

                <div style={{ display: "flex", flexWrap: "wrap" }}>
                  <Col flex half>
                    <Section.Body id="myStudyPlan" style={{ width: "max-content" }}>
                      {myPathToProgress
                        .splice(0, Math.ceil(myPathToProgress.length / 2))
                        .map((item) => (
                          <Card
                            key={item.topic_name}
                            small
                            style={{ padding: "0px 18px", marginBottom: "7px" }}
                          >
                            <Card.Body
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "15px",
                                minHeight: "50px",
                              }}
                            >
                              <StudentAttemptTitle
                                style={{
                                  flexGrow: 1,
                                  marginBottom: "2px",
                                }}
                              >
                                {item.topic_name}
                              </StudentAttemptTitle>
                              <div
                                style={{
                                  display: "flex",
                                  alignItems: "center",
                                  width: "150px",
                                }}
                              >
                                <div
                                  style={{
                                    position: "relative",
                                    width: "150px",
                                    height: "20px",
                                    backgroundColor: "#dbecf8",
                                    borderRadius: "3px",
                                  }}
                                >
                                  <div
                                    style={{
                                      position: "absolute",
                                      top: "0px",
                                      right: "6px",
                                      fontWeight: "bold",
                                      marginBottom: "1px",
                                      color:
                                        (!!item.percent_score ? parseInt(item.percent_score) : 0) >
                                        85
                                          ? "#ffffff"
                                          : "default",
                                    }}
                                  >
                                    {!!item.percent_score ? parseInt(item.percent_score) : 0}%
                                  </div>
                                  <div
                                    style={{
                                      width: `${
                                        !!item.percent_score ? parseInt(item.percent_score) : 0
                                      }%`,
                                      height: "100%",
                                      backgroundColor: CalculateProgressIndicatorColor(
                                        item.percent_score,
                                        colorRange
                                      ),
                                      borderRadius: "3px",
                                    }}
                                  />
                                </div>
                              </div>
                              <Button
                                style={{
                                  display: "block",
                                  width: "125px",
                                  flexShrink: 0,
                                  backgroundColor: "#778eef",
                                }}
                                to={{
                                  pathname: item.pathname,
                                  search: item.search,
                                  state: {
                                    from: location.pathname,
                                  },
                                }}
                              >
                                View Progress
                              </Button>
                            </Card.Body>
                          </Card>
                        ))}
                    </Section.Body>
                  </Col>

                  <Col flex half>
                    <Section.Body id="myStudyPlan" style={{ width: "max-content" }}>
                      {myPathToProgress.map((item) => (
                        <Card
                          key={item.topic_name}
                          small
                          style={{ padding: "0px 18px", marginBottom: "7px" }}
                        >
                          <Card.Body
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "20px",
                              minHeight: "50px",
                            }}
                          >
                            <StudentAttemptTitle
                              style={{
                                flexGrow: 1,
                                marginBottom: "2px",
                              }}
                            >
                              {item.topic_name}
                            </StudentAttemptTitle>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                              }}
                            >
                              <div
                                style={{
                                  position: "relative",
                                  width: "150px",
                                  height: "20px",
                                  backgroundColor: "#dbecf8",
                                  borderRadius: "3px",
                                }}
                              >
                                <div
                                  style={{
                                    position: "absolute",
                                    top: "0px",
                                    right: "6px",
                                    fontWeight: "bold",
                                    marginBottom: "1px",
                                    color:
                                      (!!item.percent_score ? parseInt(item.percent_score) : 0) > 85
                                        ? "#ffffff"
                                        : "default",
                                  }}
                                >
                                  {!!item.percent_score ? parseInt(item.percent_score) : 0}%
                                </div>
                                <div
                                  style={{
                                    width: `${
                                      !!item.percent_score ? parseInt(item.percent_score) : 0
                                    }%`,
                                    height: "100%",
                                    backgroundColor: CalculateProgressIndicatorColor(
                                      item.percent_score,
                                      colorRange
                                    ),
                                    borderRadius: "3px",
                                  }}
                                />
                              </div>
                            </div>
                            <Button
                              style={{
                                display: "block",
                                width: "125px",
                                flexShrink: 0,
                                backgroundColor: "#778eef",
                              }}
                              to={{
                                pathname: item.pathname,
                                search: item.search,
                                state: {
                                  from: location.pathname,
                                },
                              }}
                            >
                              View Progress
                            </Button>
                          </Card.Body>
                        </Card>
                      ))}
                    </Section.Body>
                  </Col>
                </div>
              </>
            )}
          </Grid>
        </SectionBody>
      </SectionDashboard>
    ) : (
      <NoDataMessageContainer noIcon>
        {apiCallInProgress == true && <Loader />}
        {apiCallInProgress == false && (
          <NoDataMessage>
            No Student Dashboard content <br /> available at this time.
          </NoDataMessage>
        )}
      </NoDataMessageContainer>
    );
  }
}

export default connect(
  (state, ownProps) => {
    return {
      studentDetails: state.facultyDashboard.student,
      apiCallInProgress: state.facultyDashboard.apiCallInProgress,
      studentApiErrors: state.facultyDashboard.apiErrors.student,
    };
  },
  {
    getAssessmentStudentDashboardData,
    clearAssessmentStudentDashboardData,
  }
)(AssessmentDashboardStudent);
